import React from "react";
import { Pressable, ScrollView, Text, TextInput, View } from "react-native";

import { ADMIN_TOKENS } from "../../../../../styles/adminTokens";
import {
  PhoneNumberText,
  PrimaryButton,
  SearchInput,
} from "../../../../common/native";
import { styles } from "../HomeConversations.styles";
import type { TPatientOption } from "../types";
import { normalizePatientPhoneForCall } from "../utils/phones";
import type { TCommunicationsCallId } from "../../../../../services/api-communications";
import { formatPhoneNumberDisplay } from "../../../../../utils/phoneNumber";

type ActiveCallContext = {
  callId: TCommunicationsCallId;
  clientRequestId: string;
  fromE164: string | null;
  toE164: string | null;
  latestStatus: string;
  latestHangupRequestStatus: string | null;
};

type DialerCardProps = {
  isCallsTab: boolean;
  callSession: {
    stateLabel: string;
    error: string | null;
    isStartingCall: boolean;
    activeCallContext: ActiveCallContext | null;
  };
  toCountryCode: string;
  onToCountryCodeChange: (value: string) => void;
  toLocalNumber: string;
  onToLocalNumberChange: (value: string) => void;
  fromNumbers: string[];
  fromE164: string;
  onFromE164Change: (value: string) => void;
  patientSearchTerm: string;
  onPatientSearchTermChange: (value: string) => void;
  onClearPatientSearch: () => void;
  patientSearchLoading: boolean;
  patientSearchError: string | null;
  patientSearchResults: TPatientOption[];
  patientUuid: string;
  onSelectPatient: (patient: TPatientOption) => void;
  selectedPatient: TPatientOption | null;
  canStartBrowserCall: boolean;
  onStartBrowserCall: () => void;
};

const DialerCard: React.FC<DialerCardProps> = ({
  isCallsTab,
  callSession,
  toCountryCode,
  onToCountryCodeChange,
  toLocalNumber,
  onToLocalNumberChange,
  fromNumbers,
  fromE164,
  onFromE164Change,
  patientSearchTerm,
  onPatientSearchTermChange,
  onClearPatientSearch,
  patientSearchLoading,
  patientSearchError,
  patientSearchResults,
  patientUuid,
  onSelectPatient,
  selectedPatient,
  canStartBrowserCall,
  onStartBrowserCall,
}) => {
  return (
    <View style={[styles.card, !isCallsTab && styles.hiddenSection]}>
      <Text style={styles.cardTitle}>Dialer</Text>

      <View style={styles.dialerInlineRow}>
        <Text style={styles.dialerInlineLabel}>From:</Text>
        <ScrollView
          horizontal
          contentContainerStyle={styles.dialerInlineOptionsContainer}
          showsHorizontalScrollIndicator={false}
          style={styles.dialerInlineValueScroll}
        >
          {fromNumbers.map((numberValue) => (
            <Pressable
              key={numberValue}
              style={[
                styles.inlineOptionButton,
                fromE164 === numberValue && styles.inlineOptionButtonActive,
              ]}
              onPress={() => onFromE164Change(numberValue)}
            >
              <Text
                style={[
                  styles.inlineOptionText,
                  fromE164 === numberValue && styles.inlineOptionTextActive,
                ]}
              >
                {formatPhoneNumberDisplay(numberValue, {
                  fallback: numberValue,
                })}
              </Text>
            </Pressable>
          ))}
          {!fromNumbers.length ? (
            <Text style={styles.emptyText}>
              No outbound caller IDs are assigned to this clinic.
            </Text>
          ) : null}
        </ScrollView>
      </View>

      <View style={styles.dialerInlineRow}>
        <Text style={styles.dialerInlineLabel}>To:</Text>
        <View style={styles.dialerInlineToInputs}>
          <TextInput
            value={toCountryCode}
            onChangeText={onToCountryCodeChange}
            placeholder="+1"
            placeholderTextColor={ADMIN_TOKENS.color.textMuted}
            style={[styles.input, styles.dialerToCountryCodeInput]}
            keyboardType="phone-pad"
            autoCapitalize="none"
            autoCorrect={false}
            accessibilityLabel="To number country code"
          />
          <TextInput
            value={toLocalNumber}
            onChangeText={onToLocalNumberChange}
            placeholder="4155550123"
            placeholderTextColor={ADMIN_TOKENS.color.textMuted}
            style={[styles.input, styles.dialerToLocalInput]}
            keyboardType="phone-pad"
            autoCapitalize="none"
            autoCorrect={false}
            accessibilityLabel="To number local digits"
          />
        </View>
      </View>

      <Text style={styles.inputLabel}>Patient Search (Optional)</Text>
      <SearchInput
        value={patientSearchTerm}
        onChangeText={onPatientSearchTermChange}
        onClear={onClearPatientSearch}
        loading={patientSearchLoading}
        placeholder="Search patients by name, email, or phone..."
        placeholderTextColor={ADMIN_TOKENS.color.textMuted}
        containerStyle={styles.patientSearchInputContainer}
        inputStyle={styles.patientSearchInput}
        iconColor={ADMIN_TOKENS.color.textMuted}
      />

      {patientSearchTerm.trim() ? (
        patientSearchError ? (
          <Text style={styles.inlineErrorText}>{patientSearchError}</Text>
        ) : patientSearchResults.length ? (
          <View style={styles.patientSearchResults}>
            {patientSearchResults.map((patient) => {
              const patientPhone = normalizePatientPhoneForCall(patient.phone);
              const hasCallablePhone = !!patientPhone;
              const isSelected = patientUuid === patient.uuid;

              return (
                <Pressable
                  key={patient.uuid}
                  style={[
                    styles.patientSearchResultRow,
                    isSelected && styles.patientSearchResultRowSelected,
                  ]}
                  onPress={() => onSelectPatient(patient)}
                >
                  <Text style={styles.callNumberText}>
                    {patient.displayName}
                  </Text>
                  <Text style={styles.metaText}>
                    {patient.email || `UUID: ${patient.uuid}`}
                  </Text>
                  <Text
                    style={
                      hasCallablePhone
                        ? styles.metaText
                        : styles.inlineErrorText
                    }
                  >
                    {hasCallablePhone
                      ? `Phone: ${formatPhoneNumberDisplay(patientPhone)}`
                      : "No phone attached to this patient record"}
                  </Text>
                </Pressable>
              );
            })}
          </View>
        ) : (
          <Text style={styles.emptyText}>No matching patients found.</Text>
        )
      ) : null}

      {selectedPatient ? (
        <View style={styles.selectedPatientPanel}>
          <Text style={styles.callNumberText}>
            Selected patient: {selectedPatient.displayName}
          </Text>
          <Text style={styles.metaText}>UUID: {selectedPatient.uuid}</Text>
          <Text
            style={
              normalizePatientPhoneForCall(selectedPatient.phone)
                ? styles.metaText
                : styles.inlineErrorText
            }
          >
            {normalizePatientPhoneForCall(selectedPatient.phone)
              ? `Phone on file: ${formatPhoneNumberDisplay(normalizePatientPhoneForCall(selectedPatient.phone))}`
              : "This record has no phone attached. Add a phone in Patients before calling."}
          </Text>
        </View>
      ) : null}

      {callSession.error ? (
        <Text style={styles.inlineErrorText}>{callSession.error}</Text>
      ) : null}

      <PrimaryButton
        text="Dial"
        onPress={onStartBrowserCall}
        loading={callSession.isStartingCall}
        disabled={!canStartBrowserCall}
        fullWidth
      />

      {callSession.activeCallContext ? (
        <View style={styles.liveCallPanel}>
          <Text style={styles.callNumberText}>Active Browser Call</Text>
          <Text style={styles.metaText}>
            Call ID: {callSession.activeCallContext.callId}
          </Text>
          <Text style={styles.metaText}>
            Request ID: {callSession.activeCallContext.clientRequestId}
          </Text>
          <Text style={styles.metaText}>
            From:{" "}
            <PhoneNumberText
              value={callSession.activeCallContext.fromE164}
              style={styles.metaText}
            />
          </Text>
          <Text style={styles.metaText}>
            To:{" "}
            <PhoneNumberText
              value={callSession.activeCallContext.toE164}
              style={styles.metaText}
            />
          </Text>
          <Text style={styles.metaText}>
            Status: {callSession.activeCallContext.latestStatus}
          </Text>
          <Text style={styles.metaText}>
            Hangup request:{" "}
            {callSession.activeCallContext.latestHangupRequestStatus || "none"}
          </Text>
        </View>
      ) : null}
    </View>
  );
};

export default DialerCard;
